/**
 * Der Verlauf über die Monate im Essenz-Spiegel — die reinen Regeln.
 * (29.09.2026, Ticket 2789)
 *
 * Martin: *„Stimmung und die weiteren Tagesparameter als Diagramm über
 * Wochen und Monate, damit sichtbar wird, wie sich mein Befinden und
 * Verhalten langfristig entwickelt.“*
 *
 * Gezeigt wird nur, was als ZAHL gemessen vorliegt:
 *   - Verfassung 1 … 5: dieselben Punkte wie in der Zeile eines Tages
 *     (`punkteAusValenz` aus `mood_days.valenz`, Nachtlauf).
 *   - Geist und Seele 0 … 100: `mood_days.geist` / `.seele`, dieselbe
 *     Bewertung, die die Verfassungs-Seite als Trio zeigt.
 *   - Körper 0 … 100 %: die WHOOP-Erholung des Tages
 *     (`environment_daily.whoop_erholung`) — nur für das Konto, dem diese
 *     Reihe gehört (server/environment-daily.ts).
 *
 * ⚠️ Der Stimmklang fehlt mit Absicht: Er liegt nur als WORT vor („ruhig“,
 *    „gelöst“). Ihm eine Zahl zuzuordnen hieße, eine Messung zu erfinden.
 *
 * ⚠️ Ein Tag ohne Wert ist eine LÜCKE, keine Null und keine Mitte. Die
 *    Wochenlinie bricht dort ab, wo eine ganze Woche nichts trägt, statt
 *    über sie hinwegzuziehen.
 */
import { MONATE, MONATE_KURZ, istTag, punkteAusValenz } from "./spiegel-zeitleiste.js";

type Kenngroesse = "verfassung" | "geist" | "seele" | "erholung";

/** Was ein Tag trägt — jedes Feld eine Messung oder null. */
export interface Tageswert {
  day: string;
  /** 1 … 5 (ganze Punkte), wie in der Zeile des Tages. */
  verfassung: number | null;
  /** 0 … 100 */
  geist: number | null;
  /** 0 … 100 */
  seele: number | null;
  /** 0 … 100 (Prozent) */
  erholung: number | null;
}

interface KenngroesseDef {
  key: Kenngroesse;
  name: string;
  /** Was die Zahl misst und auf welcher Skala — steht neben dem Namen. */
  skala: string;
  min: number;
  max: number;
}

/** Feste Reihenfolge — die Farbe folgt dem Namen, nie dem Rang. */
export const KENNGROESSEN: readonly KenngroesseDef[] = [
  { key: "verfassung", name: "Verfassung", skala: "Punkte 1–5", min: 1, max: 5 },
  { key: "geist", name: "Geist", skala: "Klarheit 0–100", min: 0, max: 100 },
  { key: "seele", name: "Seele", skala: "Grundton 0–100", min: 0, max: 100 },
  { key: "erholung", name: "Körper", skala: "Erholung 0–100 %", min: 0, max: 100 },
];

/* ------------------------------------------------------------------ *
 * Tage rechnen — als Kalendertage, nie über die Ortszeit des Geräts
 * ------------------------------------------------------------------ */

const TAG_MS = 86_400_000;

function zuMs(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}
function ausMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Tag + n Kalendertage. */
function plusTage(day: string, n: number): string {
  return ausMs(zuMs(day) + n * TAG_MS);
}

/** Abstand in ganzen Tagen (bis − von). */
function tageAbstand(von: string, bis: string): number {
  return Math.round((zuMs(bis) - zuMs(von)) / TAG_MS);
}

/**
 * Der erste Tag eines Fensters von `monate` Monaten, das mit `heute` endet:
 * derselbe Tag im Monat, `monate` zurück, plus einen Tag. Gibt es den Tag
 * im Zielmonat nicht (31. → Februar), gilt dessen letzter.
 */
export function fensterBeginn(heute: string, monate: number): string {
  const [j, m, t] = heute.split("-").map(Number) as [number, number, number];
  const ziel = new Date(Date.UTC(j, m - 1 - monate, 1));
  const letzter = new Date(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0)).getUTCDate();
  const tag = Math.min(t, letzter);
  return plusTage(ausMs(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth(), tag)), 1);
}

/* ------------------------------------------------------------------ *
 * Ein Tag aus den Quellen
 * ------------------------------------------------------------------ */

function prozent(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.max(0, Math.min(100, v));
}

/**
 * Ein Tag aus Bewertung (Nachtlauf) und Erholung — oder null, wenn er
 * keine einzige Zahl trägt. Ein leerer Tag reist nicht mit.
 */
export function tageswertAus(
  day: string,
  bewertung: { valenz?: number | null; geist?: number | null; seele?: number | null } | null,
  erholung: number | null,
): Tageswert | null {
  if (!istTag(day)) return null;
  const w: Tageswert = {
    day,
    verfassung: punkteAusValenz(bewertung?.valenz),
    geist: prozent(bewertung?.geist),
    seele: prozent(bewertung?.seele),
    erholung: prozent(erholung),
  };
  const hatWert = KENNGROESSEN.some((k) => w[k.key] != null);
  return hatWert ? w : null;
}

/* ------------------------------------------------------------------ *
 * Zeitraum
 * ------------------------------------------------------------------ */

export const ZEITRAEUME: ReadonlyArray<{ monate: number; name: string }> = [
  { monate: 3, name: "3 Monate" },
  { monate: 6, name: "6 Monate" },
  { monate: 12, name: "1 Jahr" },
];

/** So weit reicht der längste Zeitraum — der Server liefert genau ihn. */
export const LAENGSTER_ZEITRAUM = 12;

/**
 * Welche Zeiträume zeigen etwas NEUES? Der kürzeste immer; jeder längere
 * nur, wenn es Werte vor dem Beginn des nächstkürzeren gibt — sonst wäre er
 * ein Knopf, der dasselbe Bild noch einmal zeigt. Vorgabe ist der längste
 * angebotene bis zu einem halben Jahr.
 */
export function zeitraumWahl(ersterTag: string | null, heute: string): { angebote: number[]; vorgabe: number } {
  const kuerzester = ZEITRAEUME[0]?.monate ?? 3;
  const angebote = [kuerzester];
  if (ersterTag && istTag(ersterTag)) {
    for (let i = 1; i < ZEITRAEUME.length; i++) {
      const vorher = ZEITRAEUME[i - 1]?.monate ?? 0;
      const dieser = ZEITRAEUME[i]?.monate ?? 0;
      if (ersterTag < fensterBeginn(heute, vorher)) angebote.push(dieser);
    }
  }
  const vorgabe = Math.max(...angebote.filter((m) => m <= 6));
  return { angebote, vorgabe };
}

/* ------------------------------------------------------------------ *
 * Der Verlauf
 * ------------------------------------------------------------------ */

/** Ein Tag mit Wert: `i` ist sein Platz ab dem ersten Tag des Bildes. */
interface VerlaufPunkt {
  i: number;
  day: string;
  wert: number;
}

/** Ein Wochenmittel: `x` ist der Schwerpunkt der Tage, die es tragen. */
interface WochenPunkt {
  x: number;
  wert: number;
  n: number;
}

interface MonatsSpalte {
  /** YYYY-MM */
  monat: string;
  name: string;
  kurz: string;
  jahr: number;
  /** erster und letzter Platz dieses Monats im Bild */
  i0: number;
  i1: number;
}

interface MonatsMittel {
  monat: string;
  mittel: number | null;
  /** Tage mit Wert in diesem Monat (im Bild) */
  n: number;
}

interface VerlaufReihe {
  def: KenngroesseDef;
  punkte: VerlaufPunkt[];
  /** Zusammenhängende Wochen je Stück; eine Woche ohne Wert trennt. */
  wochen: WochenPunkt[][];
  monate: MonatsMittel[];
}

export interface Verlauf {
  von: string;
  bis: string;
  /** Tage im Bild, einschließlich von und bis */
  n: number;
  monate: MonatsSpalte[];
  /** Nur Kenngrößen mit mindestens einem Wert im Zeitraum. */
  reihen: VerlaufReihe[];
  /** Plätze mit mindestens einem Wert, aufsteigend (für Tippen und Tasten). */
  gemessen: number[];
}

function mittel(xs: readonly number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** Montag der Woche eines Tages, als Platz im Bild (darf negativ sein). */
function wochenMontag(i: number, day: string): number {
  const wt = new Date(zuMs(day)).getUTCDay(); // 0 = Sonntag
  return i - ((wt + 6) % 7);
}

/**
 * Das Bild eines Zeitraums: Punkte je Tag, Wochenmittel, Monatsmittel.
 * Beginn ist der erste Tag im Zeitraum, der überhaupt etwas trägt — die
 * Zeit davor ist kein Loch in einer Messung, sondern die Zeit vor ihr.
 * Ende ist immer `heute`. Nichts im Zeitraum → null.
 */
export function baueVerlauf(
  werte: readonly Tageswert[],
  heute: string,
  monate: number,
): Verlauf | null {
  if (!istTag(heute)) return null;
  const beginn = fensterBeginn(heute, monate);
  const jeTag = new Map<string, Tageswert>();
  for (const w of werte) {
    if (w && istTag(w.day) && w.day >= beginn && w.day <= heute) jeTag.set(w.day, w);
  }
  const zahl = (w: Tageswert, k: Kenngroesse): number | null => {
    const v = w[k];
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  };
  const mitWert = [...jeTag.values()]
    .filter((w) => KENNGROESSEN.some((k) => zahl(w, k.key) != null))
    .sort((a, b) => (a.day < b.day ? -1 : 1));
  const erster = mitWert[0];
  if (!erster) return null;
  const von = erster.day;
  const n = tageAbstand(von, heute) + 1;

  const spalten: MonatsSpalte[] = [];
  for (let i = 0; i < n; ) {
    const day = plusTage(von, i);
    const [j, m] = day.split("-").map(Number) as [number, number];
    const monatsEnde = ausMs(Date.UTC(j, m, 0));
    const i1 = Math.min(n - 1, tageAbstand(von, monatsEnde));
    spalten.push({
      monat: day.slice(0, 7),
      name: MONATE[m - 1] ?? "",
      kurz: MONATE_KURZ[m - 1] ?? "",
      jahr: j,
      i0: i,
      i1,
    });
    i = i1 + 1;
  }

  const gemessen = new Set<number>();
  const reihen: VerlaufReihe[] = [];
  for (const def of KENNGROESSEN) {
    const punkte: VerlaufPunkt[] = [];
    for (const w of mitWert) {
      const v = zahl(w, def.key);
      if (v == null) continue;
      punkte.push({ i: tageAbstand(von, w.day), day: w.day, wert: Math.max(def.min, Math.min(def.max, v)) });
    }
    if (!punkte.length) continue;
    for (const p of punkte) gemessen.add(p.i);

    // Wochen: Montag bis Sonntag, der Schwerpunkt der Tage mit Wert.
    const jeWoche = new Map<number, VerlaufPunkt[]>();
    for (const p of punkte) {
      const mo = wochenMontag(p.i, p.day);
      const liste = jeWoche.get(mo) ?? [];
      liste.push(p);
      jeWoche.set(mo, liste);
    }
    const wochen: WochenPunkt[][] = [];
    let stueck: WochenPunkt[] = [];
    let letzteWoche: number | null = null;
    for (const mo of [...jeWoche.keys()].sort((a, b) => a - b)) {
      const ps = jeWoche.get(mo) ?? [];
      if (letzteWoche != null && mo - letzteWoche > 7 && stueck.length) {
        wochen.push(stueck);
        stueck = [];
      }
      stueck.push({ x: mittel(ps.map((p) => p.i)), wert: mittel(ps.map((p) => p.wert)), n: ps.length });
      letzteWoche = mo;
    }
    if (stueck.length) wochen.push(stueck);

    const monateMittel: MonatsMittel[] = spalten.map((s) => {
      const drin = punkte.filter((p) => p.i >= s.i0 && p.i <= s.i1).map((p) => p.wert);
      return { monat: s.monat, mittel: drin.length ? mittel(drin) : null, n: drin.length };
    });
    reihen.push({ def, punkte, wochen, monate: monateMittel });
  }

  return {
    von,
    bis: heute,
    n,
    monate: spalten,
    reihen,
    gemessen: [...gemessen].sort((a, b) => a - b),
  };
}

/**
 * Der Platz mit Wert, der `i` am nächsten liegt (bei Gleichstand der
 * spätere) — Tippen trifft einen Tag, nicht die Lücke daneben.
 */
export function naechsterGemessener(gemessen: readonly number[], i: number): number | null {
  let best: number | null = null;
  for (const g of gemessen) {
    if (best == null || Math.abs(g - i) <= Math.abs(best - i)) best = g;
  }
  return best;
}

/** Deutsche Zahl: „3,4“ / „63“. */
export function zahlDe(v: number, nachkomma: 0 | 1): string {
  return v.toFixed(nachkomma).replace(".", ",");
}

/** Der Wert EINES Tages: „4 von 5“, „63“, „71 %“. */
export function tageswertText(def: KenngroesseDef, v: number): string {
  if (def.key === "verfassung") return `${Math.round(v)} von 5`;
  if (def.key === "erholung") return `${Math.round(v)} %`;
  return String(Math.round(v));
}

/** Ein Mittel: die Verfassung mit einer Nachkommastelle, sonst ganz. */
export function mittelText(def: KenngroesseDef, v: number): string {
  return zahlDe(v, def.key === "verfassung" ? 1 : 0);
}
